// The match screen (docs/platform/ClientShell.md): receives the worker's views, keeps the newest
// frame, and once per animation frame updates the binder (posting channel changes to the worker),
// the camera, the renderer, the HUD and the audio.
import type { FromWorker, ToWorker } from '../sim/messages.ts';
import type { PageTrace } from '../sim/trace.ts';
import { decodeFrame, type FrameView, type SceneView, type SoundEvent } from '../sim/view.ts';
import { audio } from './audio.ts';
import { CH } from './channels.ts';
import { Hud, type TeamInfo } from './hud.ts';
import { Binder } from './input.ts';
import { Renderer } from '../render/renderer.ts';

export class MatchScreen {
  root: HTMLElement;
  canvas: HTMLCanvasElement;
  renderer: Renderer;
  hud: Hud;
  binder = new Binder();
  /** The client's one audio system (game/audio.ts), shared with the front end's music. */
  audio = audio;
  /** The newest frame's view bytes, decoded when `view` is read (a frame that a newer one replaces
   * before the next animation frame is never decoded). */
  private frameBytes: Uint8Array | null = null;
  private decoded: FrameView | null = null;
  /** Every step's sound events, camera and particle views that arrived since `decoded`. */
  private pending = { events: [] as SoundEvent[], cams: [] as Uint8Array[], fx: [] as Uint8Array[], anims: [] as Uint8Array[] };
  private scene: SceneView | null = null;
  private status: HTMLElement;
  private statusText: string | null = null;
  /** The pointer capture hint (no original counterpart: the original's cursor is always trapped
   * in play, a browser captures it only from a press). */
  private hint: HTMLElement;
  private hintText: string | null = null;
  /** A match of the room's lobby (false: the offline replay of the frame tests, which takes no
   * pointer input). */
  private online = false;
  private lastTime = performance.now();
  private running = false;
  private lastTeam = -1;
  driving = false;
  blocked: string | null = null;
  /** Diagnostics for tests (window.__wmd): counters and the checkpoint CRCs this client reported. */
  stats = { frames: 0, views: 0, lands: 0, tick: 0, crcs: [] as { tick: number; crc: number }[] };
  /** The pacing trace's page half (trace.ts), when on. */
  trace: PageTrace | null = null;
  post: (m: ToWorker) => void;

  constructor(parent: HTMLElement, post: (m: ToWorker) => void, theme: string | null) {
    this.post = post;
    this.root = document.createElement('div');
    this.root.className = 'match';
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'match-canvas';
    this.root.appendChild(this.canvas);
    this.status = document.createElement('div');
    this.status.className = 'match-status';
    this.root.appendChild(this.status);
    this.hint = document.createElement('div');
    this.hint.className = 'match-hint';
    this.root.appendChild(this.hint);
    parent.appendChild(this.root);
    this.renderer = new Renderer(this.canvas);
    this.renderer.theme = theme;
    this.hud = new Hud(this.root);
    // The WormHUD objects step with every sim step's camera view (the arrow's camera centring).
    this.renderer.onCameraStep = tick => this.hud.stepWormsTo(tick, this.renderer.cameraMan);
    // A pick is the sim's WeaponPanelPick event of this client's next driven tick (the worker
    // drops it when this client does not drive): docs/port/input-frame.md. A crafting button
    // press goes into this client's next driven frame or to the server (netcode.md §7).
    this.hud.onPick = (equip, craftingSlot) => this.post({ type: 'pick', equip, craftingSlot });
    this.hud.onCraft = (team, action, equip) => this.post({ type: 'craft', team, action, equip });
    this.hud.onPanelTeam = team => this.post({ type: 'panelTeam', team });
    this.hud.onContinue = () => this.post({ type: 'send', msg: { t: 'Continue' } });
    this.binder.attach(this.canvas);
    // The camera reads the local camera channels once per sim step (render/camera.ts).
    this.renderer.cameraMan.readInput = out => this.binder.cameraInput(out);
    this.canvas.addEventListener('mousedown', e => {
      this.audio.unlock();
      if (e.button === 2) this.hud.togglePanel();
    });
    // A press on the HUD that closes the weapons panel (a pick, the right button) captures the
    // pointer again: the original traps the cursor as soon as no local menu is open
    // (`QtShell_App::SetMouseCursorVisibility` 0xa79330), a browser only during a gesture.
    this.root.addEventListener('mousedown', e => {
      if (e.target !== this.canvas && this.online && this.decoded && !this.cursorNeeded()) this.binder.lock();
    });
    window.addEventListener('keydown', () => this.audio.unlock(), { once: true });
    window.addEventListener('resize', () => this.renderer.resize());
    void this.audio.load();
  }

  /** `online`: a match of the room's lobby (false: the offline replay of the frame tests). */
  setTeams(teams: TeamInfo[], online: boolean): void {
    this.hud.setTeams(teams, online);
    this.online = online;
    // A press refusal plays on the requester's machine only (offline every team is local).
    this.audio.localTeam = teams.some(t => t.mine) ? (team: number) => !!teams[team]?.mine : null;
    // The building rules of a network match see the local teams' worms (NetSetup::IsLocalWorm).
    this.renderer.setLocalTeams(this.audio.localTeam);
  }

  /** The status line (every frame sets it: the DOM changes only with the text). */
  setStatus(text: string): void {
    if (text === this.statusText) return;
    this.statusText = text;
    this.status.textContent = text;
    this.status.style.display = text ? 'block' : 'none';
  }

  onWorker(m: FromWorker): void {
    const t0 = this.trace ? performance.now() : 0;
    switch (m.type) {
      case 'scene':
        this.scene = m.scene;
        void this.renderer.setScene(m.scene, this.renderer.theme);
        break;
      case 'land':
        if (m.which === 0) { this.renderer.setLandMask(m.bytes); this.stats.lands++; }
        else if (m.which === 2) this.renderer.setBakeState(m.bytes);
        break;
      case 'frame':
        this.frameBytes = m.bytes;
        this.pending.events.push(...m.events);
        this.pending.cams.push(...m.cams);
        this.pending.fx.push(...m.fx);
        this.pending.anims.push(...m.anims);
        this.driving = m.driving;
        this.hud.setViews(m.huds, m.recipes, m.panel);
        this.blocked = m.blocked;
        this.stats.views++;
        this.stats.tick = m.tick;
        break;
      case 'crc':
        this.stats.crcs.push({ tick: m.tick, crc: m.crc });
        break;
      default: break;
    }
    if (this.trace && (m.type === 'frame' || m.type === 'land')) this.trace.messages.push(performance.timeOrigin + t0, m.type === 'frame' ? 1 : 2, performance.now() - t0);
  }

  /** The newest frame view with every step's sound events, camera, particle and anim views not
   * consumed yet (frame() consumes them: the audio the events, the renderer the steps), which
   * carry over to a newer frame view. */
  private get view(): FrameView | null {
    if (this.frameBytes) {
      const v = decodeFrame(this.frameBytes);
      const prev = this.decoded;
      v.events = (prev?.events ?? []).concat(this.pending.events);
      v.cams = (prev?.cams ?? []).concat(this.pending.cams);
      v.fx = (prev?.fx ?? []).concat(this.pending.fx);
      v.anims = (prev?.anims ?? []).concat(this.pending.anims);
      this.frameBytes = null;
      this.pending = { events: [], cams: [], fx: [], anims: [] };
      this.decoded = v;
    }
    return this.decoded;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    const loop = (ts: number): void => {
      if (!this.running) return;
      this.frame(ts);
      requestAnimationFrame(loop);
    };
    requestAnimationFrame(loop);
  }

  stop(): void {
    this.running = false;
    this.binder.unlock();
    this.root.remove();
  }

  /** The cursor must show: the weapons panel or the results take clicks. The original shows it
   * only for a local menu (`QtShell_App::SetMouseCursorVisibility` 0xa79330); targeting keeps it
   * trapped: the HUD draws the target cursor, which the trapped mouse's motion moves. */
  private cursorNeeded(): boolean {
    return this.hud.panelOpen() || this.hud.resultsOpen();
  }

  /** The pointer capture hint (every frame sets it: the DOM changes only with the text). */
  private setHint(text: string): void {
    if (text === this.hintText) return;
    this.hintText = text;
    this.hint.textContent = text;
    this.hint.style.display = text ? 'block' : 'none';
  }

  /** `ts`: the animation frame's time (requestAnimationFrame). */
  private frame(ts: number): void {
    const now = performance.now();
    const dt = Math.min(0.1, (now - this.lastTime) / 1000);
    this.lastTime = now;
    const r = this.renderer;
    // The camera's local menus (NetGame::IsLocalMenuOpen: the round results stop its input, the
    // weapons panel its mouse); the cursor shows while a menu needs it, else a press captures it.
    const b = this.binder;
    r.cameraMan.menuOpen = this.hud.resultsOpen();
    r.cameraMan.panelOpen = this.hud.panelOpen();
    const menu = this.cursorNeeded();
    if (menu) b.unlock();
    b.captureWanted = this.online && !!this.decoded && !menu;
    b.enabled = !this.hud.panelOpen();
    // The captured pointer's motion before the channel values: a press of the same frame acts
    // after the move, in the same driven tick (the worker sums the motion per tick).
    const motion = b.takeMotion();
    if (motion) this.post({ type: 'motion', dx: motion[0], dy: motion[1] });
    if (b.update()) this.post({ type: 'input', values: b.values });
    this.setHint(!b.captureWanted || b.locked() ? ''
      : b.captureRefused ? 'The browser did not capture the mouse: NumPad 4/6/8/2 pan, the middle button centres'
      : 'Click to capture the mouse (Esc releases it)');
    const v = this.view;
    const steps = v?.cams?.length ?? 0;
    let updateMs = 0;
    let hudMs = 0;
    if (v) {
      // The panel closes when the current team changes (the panel's team is the page's player's,
      // hud.ts `panelTeam`; the client does not carry it into another turn).
      if (v.currentTeam !== this.lastTeam) {
        this.lastTeam = v.currentTeam;
        this.hud.togglePanel(false);
      }
      const t0 = performance.now();
      r.update(v, dt);
      const t1 = performance.now();
      this.hud.update(v, r);
      updateMs = t1 - t0;
      hudMs = performance.now() - t1;
      this.audio.handle(v.events);
      const pose = r.cameraMan.pose();
      this.audio.listen(pose.lookX, pose.lookY, pose.lookZ, pose.lookX - pose.x, pose.lookY - pose.y, pose.lookZ - pose.z, pose.x);
      v.events = [];
      this.setStatus(this.blocked === 'Barrier' ? 'Waiting for the other players…' : this.blocked === 'CatchUp' || this.blocked === 'Snapshot' ? 'Catching up…' : '');
    }
    const drawStart = performance.now();
    r.render();
    this.stats.frames++;
    const end = performance.now();
    this.trace?.frames.push(performance.timeOrigin + ts, v?.tick ?? 0, steps, end - now, end - drawStart, updateMs, hudMs);
  }

  /** For tests: a weapons panel pick of `equip` (as a click on its cell). */
  pickWeapon(equip: number): void {
    this.hud.onPick?.(equip, null);
  }

  /** For tests: whether the binder maps a key to a channel right now. */
  channelValue(name: string): number {
    return this.binder.values[CH[name]] ?? 0;
  }
}
